import { CheckCircle2, Clock3, Globe2, MessageCircle, Phone, School } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { fetchConfig, trackEvent } from "../api";
import type { Locale, SiteConfig, VariantId } from "../types";
import { cloneDefaultConfig } from "../config";

const iconMap = {
  globe: Globe2,
  phone: Phone,
  clock: Clock3,
  school: School
};

function readCookie(name: string) {
  return document.cookie.split("; ").find((item) => item.startsWith(`${name}=`))?.split("=")[1];
}

function setCookie(name: string, value: string, days: number) {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function chooseVariant(config: SiteConfig): VariantId {
  const forced = new URLSearchParams(location.search).get("variant");
  if (forced === "a" || forced === "b") return forced;
  const existing = readCookie("lp_variant");
  if (existing === "a" || existing === "b") return existing;
  if (!config.ab.enabled) return config.ab.defaultVariant;
  const total = Math.max(1, config.ab.ratioA + config.ab.ratioB);
  return Math.random() * total < config.ab.ratioA ? "a" : "b";
}

function openChat(config: SiteConfig, variant: VariantId, locale: Locale) {
  void trackEvent(variant, "contact", locale);
  if (typeof window.fbq === "function" && config.pixel.enabled && config.pixel.contact) {
    window.fbq("track", "Contact");
  }
  window.ssq = window.ssq || [];
  window.ssq.push("chatOpen");
}

declare global {
  interface Window {
    ssq?: unknown[];
    fbq?: (...args: unknown[]) => void;
  }
}

export function LandingApp() {
  const [config, setConfig] = useState<SiteConfig>(cloneDefaultConfig());
  const [locale, setLocale] = useState<Locale>(() => (localStorage.getItem("preferred_language") as Locale) || "tj");
  const [variant, setVariant] = useState<VariantId>("a");
  const page = config.pages[variant] || config.pages.a;

  useEffect(() => {
    void fetchConfig().then((next) => {
      setConfig(next);
      const selected = chooseVariant(next);
      setVariant(selected);
      setCookie("lp_variant", selected, next.ab.cookieDays);
      void trackEvent(selected, "view", locale);
    });
  }, []);

  useEffect(() => {
    localStorage.setItem("preferred_language", locale);
  }, [locale]);

  useEffect(() => {
    const channel = config.chat.channels.find((item) => item.enabled && item.type === "salesmartly" && item.scriptUrl);
    if (!config.chat.enabled || !channel || document.querySelector(`script[src="${channel.scriptUrl}"]`)) return;
    const script = document.createElement("script");
    script.src = channel.scriptUrl;
    script.defer = true;
    document.body.appendChild(script);
  }, [config.chat]);

  const palette = useMemo(() => ({
    "--primary": config.primaryColor,
    "--accent": config.accentColor,
    "--cream": config.backgroundColor
  }) as React.CSSProperties, [config]);

  return <main className="landing" style={palette}>
    <nav className="lp-nav">
      <strong>{config.brand}</strong>
      {config.showLanguageSwitch && <div className="lp-lang"><button className={locale === "tj" ? "active" : ""} onClick={() => setLocale("tj")}>TJ</button><button className={locale === "ru" ? "active" : ""} onClick={() => setLocale("ru")}>RU</button></div>}
    </nav>

    <section className={`lp-hero variant-${variant}`}>
      <div className="lp-hero-copy">
        <span>{page.heroTag[locale]}</span>
        <h1>{page.heroTitle[locale]}</h1>
        <p>{page.heroText[locale]}</p>
        <button onClick={() => openChat(config, variant, locale)}><MessageCircle size={18} /> {page.heroButton[locale]}</button>
      </div>
      <img src={page.heroImage} alt="" />
    </section>

    <section className="lp-story">
      <div><h2>{page.storyTitle[locale]}</h2><p>{page.storyText[locale]}</p></div>
      <button onClick={() => openChat(config, variant, locale)}>{page.consultButton[locale]}</button>
    </section>

    <section className="lp-benefits">
      {page.benefits.map((item) => {
        const Icon = iconMap[item.icon as keyof typeof iconMap] || CheckCircle2;
        return <article key={item.title[locale]}><Icon size={22} /><strong>{item.title[locale]}</strong><p>{item.body[locale]}</p></article>;
      })}
    </section>

    <section className="lp-lists">
      <div><h2>{locale === "tj" ? "Baroi ki muvofiq ast?" : "Komu podhodit?"}</h2>{page.audience.map((item) => <p key={item[locale]}><CheckCircle2 size={16} /> {item[locale]}</p>)}</div>
      <div><h2>{locale === "tj" ? "Talabot" : "Trebovaniya"}</h2>{page.requirements.map((item) => <p key={item[locale]}><CheckCircle2 size={16} /> {item[locale]}</p>)}</div>
    </section>

    <section className="lp-consult">
      <h2>{page.consultTitle[locale]}</h2>
      <p>{page.consultText[locale]}</p>
      <div>{page.quickQuestions.map((item) => <button key={item[locale]} onClick={() => openChat(config, variant, locale)}>{item[locale]}</button>)}</div>
      <button className="lp-main-cta" onClick={() => openChat(config, variant, locale)}><MessageCircle size={18} /> {page.consultButton[locale]}</button>
    </section>

    <section className="lp-faq">{page.faq.map((item) => <details key={item.question[locale]}><summary>{item.question[locale]}</summary><p>{item.answer[locale]}</p></details>)}</section>
    <footer>{page.footer[locale]}</footer>
    <button className="lp-float" onClick={() => openChat(config, variant, locale)}><MessageCircle size={22} /></button>
    <button className="lp-bottom" onClick={() => openChat(config, variant, locale)}>{page.consultButton[locale]}</button>
  </main>;
}
